/**
 * Whether the API has turned this build away as too old for it. Held outside
 * React so the API client can raise it from any request.
 */

let isPromptOpen = false;
let openedAt: number | undefined;
let hasTurnedAwayWrite = false;
let isDismissed = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach(listener => listener());

/**
 * Raised for every request the API turns away. Once the reader has dismissed
 * the prompt, only a write brings it back: a page polling in the background
 * would otherwise reopen it before they could copy their work out.
 */
export const reportOutdatedClient = ({ isWrite }: { isWrite: boolean }) => {
  if (isDismissed && !isWrite) {
    return;
  }
  if (!isPromptOpen) {
    isPromptOpen = true;
    openedAt = Date.now();
    hasTurnedAwayWrite = isWrite;
    notify();
  } else if (isWrite && !hasTurnedAwayWrite) {
    hasTurnedAwayWrite = true;
    notify();
  }
};

export const dismissOutdatedClientPrompt = () => {
  isDismissed = true;
  if (isPromptOpen) {
    isPromptOpen = false;
    openedAt = undefined;
    hasTurnedAwayWrite = false;
    notify();
  }
};

export const isOutdatedClientPromptOpen = () => isPromptOpen;

/**
 * Whether the prompt is open over a write turned away. Someone at the page
 * made it and needs to see that it did not save, so nothing reloads the tab
 * by itself meanwhile.
 */
export const isWritePromptOpen = () => isPromptOpen && hasTurnedAwayWrite;

/** When the prompt opened, while it is open and only reads have been turned away. */
export const getReadsOnlyPromptOpenedAt = () => (hasTurnedAwayWrite ? undefined : openedAt);

/** Tells a listener when the prompt opens or closes, and when a write is first turned away. */
export const subscribeToOutdatedClient = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
